import React from 'react';

import NavigationButton from '../NavigationButton';
import PageButtons from './components/PageButtons';
import PaginationLabel from './components/PaginationLabel';

import cx from '../../utils/classnames';
import calculatePageList from './helper';

import styles from './pagination.module.scss';

type Props = {
  /** Custom style for wrapper */
  className?: string;
  /** Paginations buttons styles */
  paginationBtnClassName?: string;
  total: number;
  /** Item offset */
  offset: number;
  /** Number of items per page */
  limit: number;
  /** Number of page buttons to display before the squeeze */
  itemBeforeSqueeze: number;
  /** Number of page buttons to display after the squeeze */
  itemAfterSqueeze: number;
  /** Page display offset of the current/active page */
  activePageDisplayOffset: number;
  /** Maximum number of page buttons without squeeze */
  maxItemWithoutSqueeze: number;
  /** Minimum gap item */
  minGapItem: number;
  /** Function to called upon button click */
  onChangePage?: (offset: number, limit: number) => void;
  /** Unit label */
  unit?: React.ReactNode;
  /** The type of navigation */
  navigationType?: 'light' | 'dark' | 'simple' | 'outlined';
  /** the total number of item which show pagination without squezzez style*/
  itemWithoutSqueeze?: number;
  paginationLabelResolver?: (reviewCount: string, totalCount: number) => React.ReactNode;
};

export default function Pagination(props: Props) {
  const {
    className,
    offset,
    limit,
    total,
    itemBeforeSqueeze,
    itemAfterSqueeze,
    activePageDisplayOffset,
    maxItemWithoutSqueeze,
    minGapItem,
    onChangePage,
    unit,
    navigationType,
    itemWithoutSqueeze,
    paginationLabelResolver,
    paginationBtnClassName,
    ...rest
  } = props;

  const activePage = Math.floor(offset / limit + 1);
  const totalPage = Math.ceil(total / limit);
  const pageList = calculatePageList({
    totalPage,
    activePage,
    maxItemWithoutSqueeze,
    itemBeforeSqueeze,
    itemAfterSqueeze,
    activePageDisplayOffset,
    minGapItem,
    itemWithoutSqueeze,
  });

  const isInvalidPage = isNaN(activePage) || isNaN(totalPage);

  const handleChangePage = (offset: number, limit: number) => {
    onChangePage?.(offset, limit);
  };

  const handlePrevChangePage = () => {
    handleChangePage(offset > limit ? offset - limit : 0, limit);
  };

  const handleNextChangePage = () => {
    handleChangePage(offset + limit, limit);
  };

  return (
    <div className={cx(styles.container, className)} {...rest}>
      <div className={cx(styles.paginationButtons, paginationBtnClassName)}>
        <NavigationButton
          className={styles.nav}
          key={0}
          onClick={handlePrevChangePage}
          navigationType={navigationType}
          isDisabled={isInvalidPage || activePage <= 1}
        />
        <PageButtons
          pageList={pageList}
          activePage={activePage}
          limit={limit}
          onChangePage={handleChangePage}
          buttonType={navigationType}
        />
        <NavigationButton
          className={styles.nav}
          key={totalPage + 1}
          direction="right"
          onClick={handleNextChangePage}
          navigationType={navigationType}
          isDisabled={isInvalidPage || activePage >= totalPage}
        />
      </div>
      {navigationType !== 'outlined' && (
        <PaginationLabel
          offset={offset}
          limit={limit}
          total={total}
          activePage={activePage}
          unit={unit}
          paginationLabelResolver={paginationLabelResolver}
        />
      )}
    </div>
  );
}

Pagination.defaultProps = {
  itemBeforeSqueeze: 1,
  itemAfterSqueeze: 1,
  activePageDisplayOffset: 1,
  maxItemWithoutSqueeze: 5,
  minGapItem: 2,
};
